import { Link } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { ErrorState } from '../ui/State';
import { dateTimeLocalInputValue, londonDateTimeInputInstant } from '../dates';
export function OperationsVisitChanges({ visit, onSaved }: { visit: any; onSaved: () => Promise<void> }) {
  const [proposal, setProposal] = useState<any>({ ...(visit.pendingChange?.proposal ?? visit.proposal), activities: visit.proposal.activities }),
    [preview, setPreview] = useState<any>(),
    [impact, setImpact] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false),
    [split, setSplit] = useState(visit.pendingChange?.kind === 'Split'),
    [selected, setSelected] = useState<string[]>(visit.pendingChange?.proposal.activities.map((r: any) => r.kind + ':' + r.id) ?? []),
    [splitVisit, setSplitVisit] = useState(''),
    [outside, setOutside] = useState(false),
    [pending, setPending] = useState(false);
  const receipt = useRef<{ body: string; key: string } | undefined>(undefined);
  useEffect(() => {
    void api<any>('/api/operations/visits/' + visit.id + '/impacts')
      .then(setImpact)
      .catch(setError);
  }, [visit.id, visit.version]);
  const effective = () => ({ ...proposal, activities: split ? proposal.activities.filter((r: any) => selected.includes(r.kind + ':' + r.id)) : proposal.activities });
  const splitId = useRef(crypto.randomUUID());
  async function compare() {
    setError(undefined);
    try {
      setPreview(await api('/api/operations/visits/' + visit.id + '/change-preview', { method: 'POST', body: JSON.stringify(effective()) }));
    } catch (e) {
      setError(e);
    }
  }
  async function save(command: string, extra: any = {}) {
    setBusy(true);
    setError(undefined);
    try {
      const b = { command, expectedVersion: visit.version, reason: extra.reason ?? 'Reviewed attendance change', ...extra },
        body = JSON.stringify(b);
      if (receipt.current?.body !== body) receipt.current = { body, key: crypto.randomUUID() };
      const result = await api<any>('/api/operations/visits/' + visit.id + '/changes', { method: 'POST', body: JSON.stringify({ ...b, idempotencyKey: receipt.current.key }) });
      receipt.current = undefined;
      if (result.splitVisitId) setSplitVisit(result.splitVisitId);
      await onSaved();
      setNotice(
        ['PrepareChange', 'PrepareSplit'].includes(command)
          ? 'Proposed change saved. The committed booking is unchanged.'
          : command === 'SetChangeReadiness'
            ? 'Readiness saved.'
            : 'Attendance change saved.'
      );
      if (command === 'ReassignUntouched' || command === 'Reschedule' || command === 'CancelAttendance' || command === 'SplitRemaining') setPreview(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const update = (next: any) => {
    setProposal(next);
    setPreview(undefined);
  };
  const pendingChange = visit.pendingChange;
  return (
    <section aria-label="Attendance changes">
      <h2>Cover and attendance changes</h2>
      {impact && (
        <p>
          Current assignment: {impact.state}. {impact.reasons.join(', ')}
        </p>
      )}
      <p>Untouched activities can move together. Started work keeps its officer and evidence; open its report to arrange remaining work.</p>
      {notice && <p role="status">{notice}</p>}
      {error !== undefined && <ErrorState error={error} />}
      {splitVisit && <Link to={'/operations/visits/' + splitVisit}>Open the split attendance</Link>}
      <label>
        <input
          type="checkbox"
          checked={split}
          onChange={(e) => {
            setSplit(e.target.checked);
            setPreview(undefined);
          }}
        />
        Split selected untouched activities into a separate attendance
      </label>
      {split && (
        <fieldset>
          <legend>Activities to move</legend>
          {visit.proposal.activities.map((r: any, i: number) => (
            <label key={r.kind + r.id}>
              <input
                type="checkbox"
                checked={selected.includes(r.kind + ':' + r.id)}
                onChange={(e) => {
                  setSelected((old) => (e.target.checked ? [...old, r.kind + ':' + r.id] : old.filter((k) => k !== r.kind + ':' + r.id)));
                  setPreview(undefined);
                }}
              />
              Activity {i + 1} · {r.kind}
            </label>
          ))}
        </fieldset>
      )}
      <label>
        New attendance starts
        <input
          type="datetime-local"
          value={dateTimeLocalInputValue(proposal.startsAt)}
          onChange={(e) => update({ ...proposal, startsAt: londonDateTimeInputInstant(e.target.value) })}
        />
      </label>
      <label>
        New attendance ends
        <input
          type="datetime-local"
          value={dateTimeLocalInputValue(proposal.endsAt)}
          onChange={(e) => update({ ...proposal, endsAt: londonDateTimeInputInstant(e.target.value) })}
        />
      </label>
      <button type="button" disabled={busy} onClick={() => void compare()}>
        Compare cover officers
      </button>
      {preview && (
        <>
          <fieldset>
            <legend>Cover officer</legend>
            {preview.candidates.map((c: any) => (
              <label key={c.id}>
                <input
                  type="radio"
                  name="cover-officer"
                  checked={proposal.officerId === c.id}
                  disabled={c.status !== 'Selectable'}
                  onChange={() => {
                    setProposal({ ...proposal, officerId: c.id });
                    setPreview({ ...preview, selectionStale: true });
                  }}
                />
                {c.name} · {c.status} {c.reasons.join(', ')}
              </label>
            ))}
          </fieldset>
          <button
            type="button"
            disabled={busy || preview.selectionStale}
            onClick={() => void save(split ? 'PrepareSplit' : 'PrepareChange', { proposal: effective(), ...(split ? { newVisitId: splitId.current } : {}) })}
          >
            Save proposed change
          </button>
        </>
      )}
      {pendingChange && preview && (
        <>
          <h3>Readiness for the proposed change</h3>
          {['site_access', 'tools', 'reporting'].map((check) => (
            <form
              key={check}
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                void save('SetChangeReadiness', { check, state: 'Ready', dependency: preview.dependencies[check], basis: f.get('basis') });
              }}
            >
              <p>
                {check.replaceAll('_', ' ')} · {pendingChange.readiness[check]?.state ?? 'Unknown'}
              </p>
              <label>
                {check.replaceAll('_', ' ')} for change
                <input name="basis" minLength={3} maxLength={500} required />
              </label>
              <button disabled={busy}>Confirm {check.replaceAll('_', ' ')} for change</button>
            </form>
          ))}
          <label>
            <input type="checkbox" checked={outside} onChange={(e) => setOutside(e.target.checked)} />
            Reviewed original due-window warning for change
          </label>
          <label>
            <input type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} />
            Reviewed pending availability for change
          </label>
          <button
            type="button"
            disabled={
              busy ||
              JSON.stringify(pendingChange.proposal) !== JSON.stringify(effective()) ||
              !preview.candidates.some((c: any) => c.id === proposal.officerId && c.status === 'Selectable')
            }
            onClick={() =>
              void save(pendingChange.kind === 'Split' ? 'SplitRemaining' : proposal.officerId === visit.proposal.officerId ? 'Reschedule' : 'ReassignUntouched', {
                selectionRevision: preview.candidates.find((c: any) => c.id === proposal.officerId)?.revision,
                acknowledgeOutsideDueWindow: outside,
                acknowledgePendingLeave: pending
              })
            }
          >
            Apply checked change
          </button>
        </>
      )}
      <details>
        <summary>Cancel future attendance</summary>
        <p>The activity requirements and reports remain open.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save('CancelAttendance', { reason: new FormData(e.currentTarget).get('reason') });
          }}
        >
          <label>
            Cancellation reason
            <input name="reason" minLength={3} maxLength={1000} required />
          </label>
          <button disabled={busy}>Cancel future attendance</button>
        </form>
      </details>
    </section>
  );
}
